/**
 * `<EncounterDetailsSection>` — Date / Type / Description summary of the
 * encounter. In edit mode the primary row's Type is a 2-option dropdown bound
 * to the form's `visitType` enum (PD-1288); Date/Description stay read-only,
 * and a dedicated "Added on" field (`noteDate` → `DocumentReference.date`,
 * PD-1083) sits beside the section title. The seed props (`encounterDate` /
 * `visitType` / `description`) hydrate a single `useFieldArray` row when the
 * array is empty so the first row's `date` keeps round-tripping through
 * `SoapNoteDto.noteDate` (see `draftToSoap.ts`).
 */
import { useEffect } from 'react'
import { Box, Group, Select, Table, Text } from '@mantine/core'
import { DateTimePicker } from '@mantine/dates'
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import type { ProgressNoteEditFormValues } from '../progressNoteEditSchema'
import { NoteHeading } from './NoteHeading'

export interface EncounterDetailsSectionProps {
  /**
   * Optional seed value used **only** when the field-array is empty at mount
   * time. Kept for backwards compatibility with the existing parents that
   * already derive these from the parent form state / DocumentReference.
   */
  encounterDate?: string
  visitType?: string
  description?: string
  readOnly?: boolean
  /** Primary row's Date cell display — the original encounter date. */
  readOnlyDateDisplay?: string
}

export function EncounterDetailsSection({
  encounterDate,
  visitType,
  description,
  readOnly = false,
  readOnlyDateDisplay,
}: EncounterDetailsSectionProps) {
  const { control, setValue } = useFormContext<ProgressNoteEditFormValues>()
  const fieldArray = useFieldArray({ control, name: 'encounterDetails' })
  const rows = useWatch({ control, name: 'encounterDetails' }) ?? []
  // The primary row's Description is the note's Chief Complaint, as in the PDF and patient app (PD-1889).
  const chiefComplaint = useWatch({ control, name: 'summary.chiefComplaint' }) ?? ''
  // The editable "Added on" (PD-1083) — datetime-local format ("YYYY-MM-DDTHH:mm").
  const noteDate = useWatch({ control, name: 'noteDate' }) ?? ''

  // Backwards-compat seed: if the form was constructed without an initial row
  // (legacy hydrate paths) seed one from the props the parents already pass.
  useEffect(() => {
    if (fieldArray.fields.length > 0) return
    if (!encounterDate && !visitType && !description) return
    fieldArray.append({
      id: crypto.randomUUID(),
      date: encounterDate ?? '',
      type: visitType ?? '',
      description: description ?? '',
    })
    // We intentionally exclude `fieldArray` from the dep array — RHF mutates
    // it in place and its identity is stable across renders. Adding it would
    // cause the effect to re-run after the append and bypass the empty-array
    // guard on a second cycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encounterDate, visitType, description, fieldArray.fields.length])

  return (
    <Box mb="xl">
      <Group mb="sm" align="center" justify="space-between">
        <NoteHeading level="section" id="encounter" label="Encounter Details" mb={0} />
        {!readOnly && (
          <Group gap="xs" align="center">
            <Text size="sm" c="dimmed">
              Added on
            </Text>
            <DateTimePicker
              size="xs"
              w={210}
              value={noteDate ? noteDate.replace('T', ' ') : null}
              onChange={(value) =>
                setValue('noteDate', value ? value.replace(' ', 'T').slice(0, 16) : '', {
                  shouldDirty: true,
                })
              }
              valueFormat="MM/DD/YYYY hh:mm A"
              // withinPortal:false renders the time dropdown INSIDE the parent
              // picker's dropdown DOM; otherwise each hour/minute/AM-PM click
              // lands outside it and Mantine closes the whole picker.
              timePickerProps={{
                format: '12h',
                withDropdown: true,
                popoverProps: { withinPortal: false },
              }}
              data-testid="note-added-on"
            />
          </Group>
        )}
      </Group>
      <Table withTableBorder withColumnBorders striped="even">
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: 220 }}>Date</Table.Th>
            <Table.Th style={{ width: 200 }}>Type</Table.Th>
            <Table.Th>Description</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {fieldArray.fields.length === 0 && readOnly && (
            <Table.Tr>
              <Table.Td colSpan={3}>
                <Text size="sm" c="dimmed">
                  No encounter recorded.
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
          {fieldArray.fields.map((field, idx) => {
            const row = rows[idx]
            const descriptionText = (idx === 0 ? chiefComplaint : row?.description)?.trim() || '-'
            const dateCell = idx === 0 && readOnlyDateDisplay ? readOnlyDateDisplay : row?.date
            if (readOnly) {
              return (
                <Table.Tr key={field.id} data-testid={`encounter-row-${idx}`}>
                  <Table.Td>{dateCell || '—'}</Table.Td>
                  <Table.Td>{row?.type || '—'}</Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {descriptionText}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )
            }
            return (
              <Table.Tr key={field.id} data-testid={`encounter-row-${idx}`}>
                {/* Date is read-only: the original encounter date must stay
                    visible; only the "Added on" field above edits `noteDate`. */}
                <Table.Td style={{ verticalAlign: 'top' }}>
                  <Text size="sm">{dateCell || '—'}</Text>
                </Table.Td>
                {/* Primary-row Type edits the form's `visitType` enum (PD-1288)
                    — auto-determined on open, doctor-overridable. allowDeselect
                    must stay false: Mantine's default lets a click on the
                    selected option null the value. */}
                <Table.Td style={{ verticalAlign: 'top' }}>
                  {idx === 0 ? (
                    <Controller
                      control={control}
                      name="visitType"
                      render={({ field }) => (
                        <Select
                          size="xs"
                          value={field.value}
                          onChange={(value) => value && field.onChange(value)}
                          onBlur={field.onBlur}
                          allowDeselect={false}
                          data={[
                            { value: 'new', label: 'Initial Visit' },
                            { value: 'follow-up', label: 'Follow-up Visit' },
                          ]}
                          data-testid="encounter-visit-type"
                        />
                      )}
                    />
                  ) : (
                    <Text size="sm">{row?.type || '—'}</Text>
                  )}
                </Table.Td>
                {/* Description is read-only: it has no SoapNoteDto slot. */}
                <Table.Td style={{ verticalAlign: 'top' }}>
                  <Text size="sm" c="dimmed">
                    {descriptionText}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table>
    </Box>
  )
}
